import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import logo from "../../images/logo.png";
import colmeia from "../../images/colmeia.svg";
import { useTranslation } from "react-i18next";
import { errorAlert, successAlert, warnAlert } from "../components/alert/defaultAlert";
import axiosInstance from "../../services/AxiosInstance";
import { ApiError } from "../components/Common/ApiError";
import Common from "../../constants/Common";
import { AUTH_COLORS } from "./authTheme";

const styles = {
  splitWrapper: {
    display: "flex",
    height: "100vh",
    overflow: "hidden",
  },
  leftPanel: {
    flex: "0 0 55%",
    background: AUTH_COLORS.brandGradient,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "60px 64px",
    position: "relative",
    overflow: "hidden",
  },
  hexPatternTop: {
    position: "absolute",
    top: "-80px",
    right: "-80px",
    width: "380px",
    height: "380px",
    opacity: 0.07,
    background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='69'%3E%3Cpolygon points='30,3 57,18 57,51 30,66 3,51 3,18' fill='none' stroke='%23fff' stroke-width='2'/%3E%3C/svg%3E") repeat`,
    backgroundSize: "60px 69px",
  },
  hexPatternBottom: {
    position: "absolute",
    bottom: "-100px",
    left: "-60px",
    width: "340px",
    height: "340px",
    opacity: 0.06,
    background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='69'%3E%3Cpolygon points='30,3 57,18 57,51 30,66 3,51 3,18' fill='none' stroke='%23fff' stroke-width='2'/%3E%3C/svg%3E") repeat`,
    backgroundSize: "60px 69px",
  },
  leftLogoContainer: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    marginBottom: "56px",
  },
  leftLogo: {
    width: "auto",
    height: "68px",
    display: "block",
  },
  leftColmeiaName: {
    height: "62px",
    width: "auto",
    filter: "brightness(0) invert(1)",
    display: "block",
    alignSelf: "center",
  },
  leftHeadline: {
    fontSize: "36px",
    fontWeight: "800",
    color: "#fff",
    lineHeight: "1.2",
    marginBottom: "16px",
    letterSpacing: "-0.5px",
    maxWidth: "420px",
  },
  leftSubheadline: {
    fontSize: "16px",
    color: AUTH_COLORS.overlayStrong,
    marginBottom: "48px",
    lineHeight: "1.6",
    maxWidth: "400px",
  },
  featureList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  featureItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
  },
  featureIconWrap: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    background: AUTH_COLORS.featureGlass,
    border: `1px solid ${AUTH_COLORS.featureBorder}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    backdropFilter: "blur(4px)",
  },
  featureTextWrap: {
    paddingTop: "2px",
  },
  featureTitle: {
    fontSize: "15px",
    fontWeight: "700",
    color: "#fff",
    marginBottom: "2px",
  },
  featureDesc: {
    fontSize: "13px",
    color: AUTH_COLORS.overlaySoft,
    lineHeight: "1.5",
  },
  rightPanel: {
    flex: 1,
    background: AUTH_COLORS.panelBackgroundAlt,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "48px 40px",
    overflowY: "auto",
  },
  formContainer: {
    width: "100%",
    maxWidth: "432px",
    animation: "slideUp 0.5s ease-out",
  },
  formEyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "9px 15px",
    borderRadius: "999px",
    background: AUTH_COLORS.eyebrowBackground,
    color: AUTH_COLORS.eyebrowText,
    border: `1px solid ${AUTH_COLORS.eyebrowBorder}`,
    boxShadow: `0 10px 24px ${AUTH_COLORS.eyebrowShadow}`,
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    marginBottom: "20px",
  },
  formHairline: {
    width: "86px",
    height: "4px",
    borderRadius: "999px",
    background: AUTH_COLORS.hairlineGradient,
    marginBottom: "24px",
  },
  mobileLogoContainer: {
    display: "none",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    marginBottom: "36px",
    marginLeft: "auto",
    marginRight: "auto",
    width: "fit-content",
  },
  mobileLogo: {
    width: "auto",
    height: "56px",
    display: "block",
  },
  mobileColmeiaName: {
    height: "52px",
    width: "auto",
    display: "block",
    alignSelf: "center",
  },
  welcomeText: {
    fontSize: "38px",
    fontWeight: "800",
    color: AUTH_COLORS.textHeading,
    marginBottom: "10px",
    letterSpacing: "-1px",
    lineHeight: "1.02",
    maxWidth: "380px",
  },
  subtitleText: {
    fontSize: "15px",
    color: AUTH_COLORS.textBody,
    marginBottom: "38px",
    lineHeight: "1.72",
    maxWidth: "360px",
  },
  inputGroup: {
    marginBottom: "22px",
    position: "relative",
  },
  inputLabel: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: AUTH_COLORS.textLabel,
    marginBottom: "8px",
    letterSpacing: "0.2px",
  },
  inputWrapper: {
    position: "relative",
  },
  inputIconLeft: {
    position: "absolute",
    left: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    color: AUTH_COLORS.textMuted,
    display: "flex",
    alignItems: "center",
    pointerEvents: "none",
    transition: "color 0.2s ease",
  },
  inputIconRight: {
    position: "absolute",
    right: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    color: AUTH_COLORS.textMuted,
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: "2px",
    transition: "color 0.2s ease",
  },
  input: {
    width: "100%",
    padding: "17px 48px 17px 48px",
    fontSize: "15px",
    border: `1.5px solid ${AUTH_COLORS.inputBorder}`,
    borderRadius: "18px",
    outline: "none",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
    backgroundColor: AUTH_COLORS.inputBackground,
    color: AUTH_COLORS.textStrong,
    boxShadow: `inset 0 1px 0 ${AUTH_COLORS.inputInset}, 0 6px 16px rgba(17, 17, 17, 0.03)`,
    boxSizing: "border-box",
  },
  inputFocus: {
    borderColor: AUTH_COLORS.accent,
    backgroundColor: AUTH_COLORS.panelBackground,
    boxShadow: `0 0 0 4px ${AUTH_COLORS.accentFocus}, 0 14px 26px rgba(17, 17, 17, 0.06)`,
  },
  alertError: {
    background: AUTH_COLORS.errorBackground,
    border: `1px solid ${AUTH_COLORS.errorBorder}`,
    borderRadius: "10px",
    padding: "12px 14px",
    marginBottom: "20px",
    color: AUTH_COLORS.errorText,
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  submitButton: {
    width: "100%",
    padding: "16px",
    fontSize: "15px",
    fontWeight: "700",
    color: "#fff",
    background: `linear-gradient(135deg, ${AUTH_COLORS.cta} 0%, ${AUTH_COLORS.ctaStrong} 100%)`,
    border: `1px solid ${AUTH_COLORS.ctaBorder}`,
    borderRadius: "18px",
    cursor: "pointer",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
    boxShadow: `0 16px 28px ${AUTH_COLORS.ctaShadow}, 0 0 0 1px ${AUTH_COLORS.ctaGlow}`,
    marginTop: "12px",
    letterSpacing: "0.4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    position: "relative",
    overflow: "hidden",
  },
  submitButtonHover: {
    transform: "translateY(-1px)",
    boxShadow: `0 18px 34px ${AUTH_COLORS.ctaShadowHover}, 0 0 0 1px ${AUTH_COLORS.ctaGlow}`,
  },
  submitButtonHighlight: {
    position: "absolute",
    left: "1px",
    right: "1px",
    top: "1px",
    height: "44%",
    borderRadius: "15px 15px 12px 12px",
    background: `linear-gradient(180deg, ${AUTH_COLORS.ctaHighlight} 0%, rgba(255,255,255,0) 100%)`,
    pointerEvents: "none",
  },
  formFooter: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    marginTop: "34px",
    paddingTop: "24px",
    borderTop: "1px solid rgba(17, 17, 17, 0.08)",
  },
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    color: AUTH_COLORS.accent,
    fontSize: "14px",
    fontWeight: "700",
    textDecoration: "none",
  },
  securityNote: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "14px",
    background: AUTH_COLORS.infoBackground,
    borderRadius: "10px",
    marginTop: "20px",
    border: `1px solid ${AUTH_COLORS.infoBorder}`,
  },
  securityNoteIcon: {
    color: AUTH_COLORS.infoText,
    flexShrink: 0,
    marginTop: "1px",
  },
  securityNoteText: {
    fontSize: "12px",
    color: AUTH_COLORS.infoText,
    lineHeight: "1.6",
    margin: 0,
  },
};

const injectCSS = `
@keyframes slideUp {
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
@media (max-width: 768px) {
  .reset-left-panel { display: none !important; }
  .reset-right-panel { padding: 40px 24px !important; }
  .reset-mobile-logo { display: flex !important; }
  .reset-welcome-text { text-align: center !important; }
}
`;

const ResetPassword = () => {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [passwordConfirmationFocused, setPasswordConfirmationFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [buttonHovered, setButtonHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uuid = searchParams.get("uuid");

  const onSubmit = (e) => {
    e.preventDefault();

    if (disabled || isLoading) {
      return;
    }

    if (password.trim() === "") {
      warnAlert(t("validation.required.password"));
      return;
    }

    if (passwordConfirmation.trim() === "") {
      warnAlert(t("validation.required.password_confirmation"));
      return;
    }

    if (password.length < Common.MINIMUM_PASSWORD_LENGTH) {
      warnAlert(t("message.minimum_password_length", { length: Common.MINIMUM_PASSWORD_LENGTH }));
      return;
    }

    if (password !== passwordConfirmation) {
      warnAlert(t("validation.invalid.password_confirmation"));
      return;
    }

    setIsLoading(true);

    axiosInstance.post("reset-password", {
      password,
      uuid,
    })
      .then(() => {
        setIsLoading(false);
        successAlert(t("message.reset_password_successfully"), () => {
          navigate("/login");
        });
      })
      .catch((error) => {
        setIsLoading(false);
        ApiError(error);
      });
  };

  useEffect(() => {
    if (!uuid) {
      setDisabled(true);
      setInvalidLink(true);
      errorAlert("O link de redefinição de senha é inválido ou expirou.");
    }
  }, [uuid]);

  const features = [
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      ),
      title: "Atualização protegida",
      desc: "Sua nova senha é enviada com o mesmo padrão de segurança usado em todo o sistema.",
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0110 0v4" />
        </svg>
      ),
      title: "Senha forte",
      desc: `Use pelo menos ${Common.MINIMUM_PASSWORD_LENGTH} caracteres para concluir a redefinição com segurança.`,
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      ),
      title: "Acesso em instantes",
      desc: "Assim que a nova senha for salva, você já pode voltar para a tela de login e acessar a conta.",
    },
  ];

  return (
    <>
      <style>{injectCSS}</style>

      <div style={styles.splitWrapper}>
        <div className="reset-left-panel" style={styles.leftPanel}>
          <div style={styles.hexPatternTop} />
          <div style={styles.hexPatternBottom} />

          <div style={styles.leftLogoContainer}>
            <img src={logo} style={styles.leftLogo} alt="Colmeia" />
            <img src={colmeia} style={styles.leftColmeiaName} alt="Colmeia" />
          </div>

          <h1 style={styles.leftHeadline}>
            Defina uma nova senha<br />com o padrão do sistema.
          </h1>
          <p style={styles.leftSubheadline}>
            Atualize seu acesso em uma interface consistente com o restante da autenticação, mantendo o mesmo fluxo seguro de redefinição.
          </p>

          <ul style={styles.featureList}>
            {features.map((feature, index) => (
              <li key={index} style={styles.featureItem}>
                <div style={styles.featureIconWrap}>{feature.icon}</div>
                <div style={styles.featureTextWrap}>
                  <div style={styles.featureTitle}>{feature.title}</div>
                  <div style={styles.featureDesc}>{feature.desc}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="reset-right-panel" style={styles.rightPanel}>
          <div style={styles.formContainer}>
            <div className="reset-mobile-logo" style={styles.mobileLogoContainer}>
              <img src={logo} style={styles.mobileLogo} alt="Colmeia" />
              <img src={colmeia} style={styles.mobileColmeiaName} alt="Colmeia" />
            </div>

            <div style={styles.formEyebrow}>
              <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: AUTH_COLORS.accent }} />
              Redefinicao segura
            </div>
            <div style={styles.formHairline} />

            <h2 className="reset-welcome-text" style={styles.welcomeText}>{t("update_password_title")}</h2>
            <p style={styles.subtitleText}>{t("message.reset_password_text")}.</p>

            {invalidLink && (
              <div style={styles.alertError}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="2" />
                  <path d="M10 6v5M10 13.5v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                O link de redefinição de senha é inválido ou expirou.
              </div>
            )}

            <form onSubmit={onSubmit} noValidate>
              <div style={styles.inputGroup}>
                <label style={styles.inputLabel}>{t("password")}</label>
                <div style={styles.inputWrapper}>
                  <span style={{ ...styles.inputIconLeft, color: passwordFocused ? AUTH_COLORS.accent : AUTH_COLORS.textMuted }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0110 0v4" />
                    </svg>
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    style={{ ...styles.input, ...(passwordFocused ? styles.inputFocus : {}) }}
                    placeholder={t("password_placeholder")}
                    autoComplete="new-password"
                    disabled={disabled || isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    style={{ ...styles.inputIconRight, color: showPassword ? AUTH_COLORS.accent : AUTH_COLORS.textMuted }}
                    tabIndex={-1}
                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                    disabled={disabled || isLoading}
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.inputLabel}>{t("password_confirmation")}</label>
                <div style={styles.inputWrapper}>
                  <span style={{ ...styles.inputIconLeft, color: passwordConfirmationFocused ? AUTH_COLORS.accent : AUTH_COLORS.textMuted }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0110 0v4" />
                    </svg>
                  </span>
                  <input
                    type={showPasswordConfirmation ? "text" : "password"}
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    onFocus={() => setPasswordConfirmationFocused(true)}
                    onBlur={() => setPasswordConfirmationFocused(false)}
                    style={{ ...styles.input, ...(passwordConfirmationFocused ? styles.inputFocus : {}) }}
                    placeholder={t("password_confirmation_placeholder")}
                    autoComplete="new-password"
                    disabled={disabled || isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordConfirmation((value) => !value)}
                    style={{ ...styles.inputIconRight, color: showPasswordConfirmation ? AUTH_COLORS.accent : AUTH_COLORS.textMuted }}
                    tabIndex={-1}
                    aria-label={showPasswordConfirmation ? "Ocultar confirmacao de senha" : "Mostrar confirmacao de senha"}
                    disabled={disabled || isLoading}
                  >
                    {showPasswordConfirmation ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={disabled || isLoading}
                style={{
                  ...styles.submitButton,
                  ...(buttonHovered && !disabled && !isLoading ? styles.submitButtonHover : {}),
                  ...(disabled || isLoading ? { opacity: 0.8, cursor: "not-allowed" } : {}),
                }}
                onMouseEnter={() => setButtonHovered(true)}
                onMouseLeave={() => setButtonHovered(false)}
              >
                <span style={styles.submitButtonHighlight} />
                {isLoading ? (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ animation: "spin 0.8s linear infinite", position: "relative", zIndex: 1 }}>
                      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3" />
                      <path d="M12 2a10 10 0 0 1 10 10" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    <span style={{ position: "relative", zIndex: 1 }}>{t("loading")}</span>
                  </>
                ) : (
                  <>
                    <span style={{ position: "relative", zIndex: 1 }}>{t("update_password")}</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ position: "relative", zIndex: 1 }}>
                      <path d="M5 12h14" />
                      <path d="m13 5 7 7-7 7" />
                    </svg>
                  </>
                )}
              </button>
            </form>

            <div style={styles.formFooter}>
              <NavLink to="/login" style={styles.backLink}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5" />
                  <path d="M12 19l-7-7 7-7" />
                </svg>
                {t("back_login")}
              </NavLink>
            </div>

            <div style={styles.securityNote}>
              <span style={styles.securityNoteIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </span>
              <p style={styles.securityNoteText}>
                Defina uma nova senha e confirme os dados antes de enviar. Se o link tiver expirado, solicite uma nova recuperação na tela de login.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
